import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useColorTheme } from '../../context/ColorThemeContext';
import GeographyLanguageSelector from '../common/GeographyLanguageSelector';
import { BrandIcon } from './BrandIcons';
import { AdPreviewPanel } from '../adcreator/AdPreviewPanel';
import BrandAssetsCard from '../adcreator/BrandAssetsCard';
import VideoPlatformPicker from '../adcreator/VideoPlatformPicker';
import { getSuggestedPresetForPlatform, getPlatformLabel } from '../../lib/videoPresets';
import { ArrowLeft } from 'lucide-react';
import { CampaignStrategy } from '../common/CampaignStrategy';
import { FeedbackInput } from '../feedback/FeedbackInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuickContentFormV2 = ({ template, onSubmit, onCancel, isGenerating, setIsGenerating, generationProgress, setGenerationProgress, onNavigate, sessionToken, setCreditPreviewData, setShowCreditPreview, setPendingGenerationData }) => {
  const { colorTheme } = useColorTheme();
  const [formData, setFormData] = useState({
    product_name: '',
    product_description: '',
    key_benefits: '',
    brand_voice: 'professional',
    price: '',
    category: '',
    product_image: '',
    product_images: [],
    avatar_image_url: '',
    avatar_image_urls: [],
    brand_logo_url: '',
    age_range_min: 25,
    age_range_max: 55,
    gender: 'All',
    geography: '',
    video_duration: 10,
    video_model: 'gemini_omni_video',
    video_platform_preset: null,
    video_resolution: '720p',
    video_aspect_ratio: '16:9',
    video_generation_mode: 'text',
    image_model: 'freepik',
    nano_banana_resolution: '4K',
    product_tagline: '',
    text_overlay_method: 'overlay',
    industry_business_type: '',
    budget_range: '',
    interests_behaviors: '',
    persona_count: 'ai_determined',
    enable_personas: false,
    website_url: '',
    languages: ['en'],
    video_languages: ['en'],
    // Campaign Strategy fields
    campaign_objective: '',
    primary_platform: 'instagram',
    cta_goal: '',
    offer_incentive: '',
    landing_page_url: '',
    pain_point: '',
    // Brand assets (auto-populated by website extraction)
    apply_logo_watermark: true,
    // Multi-language image generation (Ultra Image / GPT Image 2 only)
    generate_localized_images: false,
    // Video mode + safe-text default
    render_text_in_video: false, // OFF by default — Veo 3 often mis-spells brand names
  });
  
  const [selectedGeographies, setSelectedGeographies] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState(['en']);
  const [isExtractingWebsite, setIsExtractingWebsite] = useState(false);
  const [websiteExtractionError, setWebsiteExtractionError] = useState('');
  const [websiteExtractionSuccess, setWebsiteExtractionSuccess] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showAdPreview, setShowAdPreview] = useState(false);
  const proceedFunctionRef = useRef(null);

  // Theme helpers
  const inputClass = `w-full px-3.5 py-2.5 text-sm border rounded-xl focus:ring-2 focus:border-transparent dark:bg-slate-800/50 dark:text-white transition-colors ${
    colorTheme === 'purple' ? 'border-slate-200 dark:border-slate-700/50 focus:ring-purple-500' :
    colorTheme === 'blue' ? 'border-slate-200 dark:border-slate-700/50 focus:ring-blue-500' :
    'border-slate-200 dark:border-slate-700/50 focus:ring-emerald-500'
  }`;
  const labelClass = 'block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5';
  const cardClass = 'bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-6 shadow-sm';
  const titleClass = `text-lg font-semibold ${
    colorTheme === 'purple' ? 'text-purple-900 dark:text-purple-100' :
    colorTheme === 'blue' ? 'text-blue-900 dark:text-blue-100' :
    'text-emerald-900 dark:text-emerald-100'
  }`;
  const gradient = colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500';

  // Sync geography and language
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      geography: selectedGeographies.length > 0 ? selectedGeographies[0] : '',
      languages: selectedLanguages,
      video_languages: selectedLanguages
    }));
  }, [selectedGeographies, selectedLanguages]);

  useEffect(() => {
    if (setShowCreditPreview) setShowCreditPreview(false);
  }, [formData.video_model, formData.video_resolution, formData.video_duration, formData.image_model, formData.nano_banana_resolution, formData.enable_personas, formData.persona_count, selectedLanguages, setShowCreditPreview]);

  // Auto-enable "Generate Localized Images" the first time the user picks 2+
  // languages with a multilingual-capable model selected.
  useEffect(() => {
    const multiLang = (selectedLanguages?.length || 0) > 1;
    const supportsLocalized = ['gpt4o-image', 'nano-banana-pro', 'nano-banana-2'].includes(formData.image_model);
    if (multiLang && supportsLocalized && !formData.generate_localized_images) {
      setFormData((prev) => ({ ...prev, generate_localized_images: true }));
    }
  }, [selectedLanguages, formData.image_model, formData.generate_localized_images]);

  // Auto-suggest Multi-Reference Video mode when 2+ brand assets are uploaded.
  useEffect(() => {
    const refCount =
      ((formData.product_images?.length || 0) > 0 ? 1 : 0) +
      (formData.avatar_image_url ? 1 : 0) +
      (formData.brand_logo_url ? 1 : 0);
    if (refCount >= 2 && formData.video_generation_mode === 'text') {
      setFormData((prev) => ({ ...prev, video_generation_mode: 'multi_reference' }));
    }
  }, [formData.product_images, formData.avatar_image_url, formData.brand_logo_url, formData.video_generation_mode]);


  // Currency mapping
  const getCurrencyAndBudgetRanges = (geography) => {
    const currencyMap = {
      'United States': { currency: 'USD', symbol: '$', ranges: ['Under $100','$100-$500','$500-$1,000','$1,000-$5,000','$5,000-$10,000','$10,000+','Enterprise'] },
      'United Kingdom': { currency: 'GBP', symbol: '£', ranges: ['Under £80','£80-£400','£400-£800','£800-£4,000','£4,000-£8,000','£8,000+','Enterprise'] },
      'India': { currency: 'INR', symbol: '₹', ranges: ['Under ₹8,000','₹8,000-₹40,000','₹40,000-₹80,000','₹80,000-₹4,00,000','₹4,00,000-₹8,00,000','₹8,00,000+','Enterprise'] },
      'Australia': { currency: 'AUD', symbol: 'A$', ranges: ['Under A$150','A$150-A$750','A$750-A$1,500','A$1,500-A$7,500','A$7,500-A$15,000','A$15,000+','Enterprise'] },
      'Singapore': { currency: 'SGD', symbol: 'S$', ranges: ['Under S$140','S$140-S$700','S$700-S$1,400','S$1,400-S$7,000','S$7,000-S$14,000','S$14,000+','Enterprise'] },
      'Canada': { currency: 'CAD', symbol: 'C$', ranges: ['Under C$130','C$130-C$650','C$650-C$1,300','C$1,300-C$6,500','C$6,500-C$13,000','C$13,000+','Enterprise'] },
      'Germany': { currency: 'EUR', symbol: '€', ranges: ['Under €90','€90-€450','€450-€900','€900-€4,500','€4,500-€9,000','€9,000+','Enterprise'] },
      'France': { currency: 'EUR', symbol: '€', ranges: ['Under €90','€90-€450','€450-€900','€900-€4,500','€4,500-€9,000','€9,000+','Enterprise'] }
    };
    return currencyMap[geography] || { currency: 'USD', symbol: '$', ranges: ['Under $100','$100-$500','$500-$1,000','$1,000-$5,000','$5,000-$10,000','$10,000+','Enterprise'] };
  };

  const currentBudgetOptions = getCurrencyAndBudgetRanges(formData.geography);

  useEffect(() => {
    if (formData.geography && formData.budget_range) {
      const opts = getCurrencyAndBudgetRanges(formData.geography);
      if (!opts.ranges.includes(formData.budget_range)) {
        setFormData(prev => ({ ...prev, budget_range: '' }));
      }
    }
  }, [formData.geography]);

  // Expose proceed function
  useEffect(() => {
    window.oneClickStudioProceedWithGeneration = async () => {
      if (proceedFunctionRef.current) await proceedFunctionRef.current();
    };
    return () => {
      delete window.oneClickStudioProceedWithGeneration;
      delete window.oneClickStudioPendingData;
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'video_model') {
      let newResolution = formData.video_resolution;
      if (value === 'ultra_video' && formData.video_resolution === '4K') newResolution = '1080p';
      else if (value === 'freepik' && (formData.video_resolution === '4K' || formData.video_resolution === '1080p')) newResolution = '720p';
      setFormData(prev => ({ ...prev, [name]: value, video_resolution: newResolution }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleExtractWebsite = async () => {
    const url = formData.website_url?.trim();
    if (!url) { setWebsiteExtractionError('Please enter a website URL'); return; }
    setIsExtractingWebsite(true);
    setWebsiteExtractionError('');
    try {
      const response = await axios.post(`${API}/extract-website-content`, { url }, { headers: { Authorization: `Bearer ${sessionToken}` } });
      if (response.data.success && response.data.data) {
        const extracted = response.data.data;
        const detectedLogo = extracted.logo_url || extracted.logo_absolute_url || '';
        setFormData(prev => ({
          ...prev,
          product_name: extracted.product_name || prev.product_name,
          product_description: extracted.product_description || prev.product_description,
          key_benefits: extracted.key_benefits || prev.key_benefits,
          brand_voice: extracted.brand_voice || prev.brand_voice,
          price: extracted.price || prev.price,
          category: extracted.category || prev.category,
          product_tagline: extracted.tagline || prev.product_tagline,
          brand_logo_url: detectedLogo || prev.brand_logo_url,
          // When a logo is detected, auto-switch to GPT Image 2 ("Ultra Image")
          // which embeds the logo via image-to-image for the best brand retention.
          image_model: detectedLogo ? 'gpt4o-image' : prev.image_model
        }));
        setWebsiteExtractionSuccess(true);
      } else {
        setWebsiteExtractionError(response.data.error || 'Failed to extract website content');
      }
    } catch (err) {
      setWebsiteExtractionError(err.response?.data?.detail || 'Failed to extract website content. Please try again.');
    } finally {
      setIsExtractingWebsite(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const filesToProcess = files.slice(0, 2);
    const imagePromises = filesToProcess.map(file => new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(file);
    }));
    Promise.all(imagePromises).then(base64Images => {
      setFormData({ ...formData, product_image: base64Images[0], product_images: base64Images });
      setImagePreview(base64Images);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');
    try {
      const hasPersonas = formData.enable_personas;
      const personaCount = hasPersonas ? (formData.persona_count === 'ai_determined' ? 3 : parseInt(formData.persona_count)) : 1;
      const previewResponse = await axios.post(`${API}/preview-credit-cost`, {
        product: { ...formData, enable_personas: formData.enable_personas, persona_count: formData.persona_count || '1', image_model: formData.image_model || 'nebius', nano_banana_resolution: formData.nano_banana_resolution || '4K' },
        ad_types: [template.id]
      }, { headers: { 'Authorization': `Bearer ${sessionToken}` } });

      if (!setCreditPreviewData || !setShowCreditPreview || !setPendingGenerationData) {
        setError('Credit preview not available. Please try again.');
        return;
      }
      setCreditPreviewData(previewResponse.data);
      setPendingGenerationData({ source: 'one-click-studio', formData, template, hasPersonas: formData.enable_personas, personaCount });
      proceedFunctionRef.current = async () => { await proceedWithGeneration(); };
      window.oneClickStudioProceedWithGeneration = async () => { if (proceedFunctionRef.current) await proceedFunctionRef.current(); };
      setShowCreditPreview(true);
      return;
    } catch (err) {
      setIsSubmitting(false);
      if (err.response && err.response.status === 402) {
        const d = err.response.data;
        setError(`Insufficient credits. Need ${d.required_credits}, have ${d.current_balance}.`);
        return;
      }
      setError('Failed to calculate credit cost. Please try again.');
    }
  };

  const proceedWithGeneration = async () => {
    setIsSubmitting(true);
    window.userChoseBackgroundMode = false;
    setIsGenerating(true);
    const hasPersonas = formData.enable_personas;
    const personaCount = hasPersonas ? (formData.persona_count === 'ai_determined' ? 3 : parseInt(formData.persona_count)) : 1;
    const totalAds = 1 * personaCount;
    setGenerationProgress({ step: 0, total: totalAds, currentStage: 'initializing', currentAdType: template.id, currentPersona: '', completedAds: [], message: 'Initializing AI-powered content generation...', startTime: Date.now(), hasPersonas, personaCount, actualProgress: 0 });

    try {
      const requestData = { product: { ...formData, occupation: '' }, ad_types: [template.id] };
      // Optional free-form user instructions (from the "Additional instructions"
      // collapsible above). Empty = no override.
      if (formData.user_feedback && formData.user_feedback.trim()) {
        requestData.user_feedback = formData.user_feedback.trim();
      }
      const response = await axios.post(`${API}/generate-ads`, requestData, { headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${sessionToken}` }, timeout: 900000 });
      const campaignId = response.data.id;
      window.currentPollingCampaignId = campaignId;

      const pollCampaign = async () => {
        try {
          const statusResponse = await axios.get(`${API}/campaigns/${campaignId}/status`, { headers: { Authorization: `Bearer ${sessionToken}` } });
          const { status, progress } = statusResponse.data;
          if (setGenerationProgress) {
            let stageToSet = 'content_generation';
            if (status === 'completed') stageToSet = 'complete';
            else if (status === 'queued') stageToSet = 'initializing';
            else if (status === 'failed') stageToSet = 'error';
            setGenerationProgress(prev => ({
              ...prev, actualProgress: progress, currentStage: stageToSet,
              message: status === 'generating' ? `Generating your ${template.title}... ${progress}% complete` : status === 'completed' ? 'Campaign generation complete!' : status === 'queued' ? 'Campaign queued for generation...' : status === 'failed' ? 'Campaign generation failed' : 'Processing...'
            }));
          }
          if (status === 'completed') {
            await new Promise(resolve => setTimeout(resolve, 1500));
            try {
              const completeCampaignResponse = await axios.get(`${API}/campaigns/${campaignId}`, { headers: { Authorization: `Bearer ${sessionToken}` } });
              onSubmit(completeCampaignResponse.data);
              if (window.userChoseBackgroundMode !== true) { setIsGenerating(false); setIsSubmitting(false); }
            } catch (fetchError) {
              onSubmit(response.data);
              setIsGenerating(false);
              setIsSubmitting(false);
            }
            window.currentPollingCampaignId = null;
            return true;
          } else if (status === 'failed') {
            setError('Campaign generation failed. Please try again.');
            setIsGenerating(false);
            setIsSubmitting(false);
            window.currentPollingCampaignId = null;
            return true;
          }
          return false;
        } catch (pollErr) { return false; }
      };

      const pollInterval = setInterval(async () => { if (await pollCampaign()) clearInterval(pollInterval); }, 2000);
      if (await pollCampaign()) clearInterval(pollInterval);
      return;
    } catch (err) {
      let errorMessage = 'Failed to generate content. Please try again.';
      if (err.response?.status === 429) { const detail = err.response.data?.detail || 'Please wait for your previous campaign to complete.'; errorMessage = detail; alert(`Ultra Video In Progress\n\n${detail}`); }
      else if (err.code === 'ECONNABORTED') errorMessage = 'Generation is taking longer than expected. Content might still be processing.';
      else if (err.response?.data) { const d = err.response.data; if (typeof d.detail === 'string') errorMessage = d.detail; else if (Array.isArray(d.detail)) errorMessage = d.detail.map(e => e.msg || e.type || 'Invalid field').join(', '); else if (d.message) errorMessage = d.message; }
      setError(errorMessage);
      setGenerationProgress(prev => ({ ...prev, currentStage: 'error', message: `Error generating ${template.title}`, actualProgress: 0 }));
      setIsGenerating(false);
      setIsSubmitting(false);
    }
  };

  // ========== JSX ==========
  return (
    <div className={`min-h-screen bg-gradient-to-br ${
      colorTheme === 'purple' ? 'from-purple-50/80 via-white to-pink-50/50' :
      colorTheme === 'blue' ? 'from-blue-50/80 via-white to-sky-50/50' :
      'from-emerald-50/80 via-white to-teal-50/50'
    } dark:from-slate-900 dark:via-slate-900 dark:to-slate-900`} data-testid="quick-content-form">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button onClick={onCancel} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-700/50 flex items-center justify-center">
              <BrandIcon templateId={template.id} size={22} />
            </div>
            <div>
              <h1 className={`text-2xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>
                {template.title}
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">{template.description}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="quick-content-form-element">

          {/* Website URL Auto-Fill */}
          <div className={`p-5 rounded-2xl border backdrop-blur-xl ${
            colorTheme === 'purple' ? 'bg-purple-50/50 dark:bg-purple-900/10 border-purple-200/50 dark:border-purple-700/30' :
            colorTheme === 'blue' ? 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-200/50 dark:border-blue-700/30' :
            'bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200/50 dark:border-emerald-700/30'
          }`} data-testid="website-url-section">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🌐</span>
              <h4 className="text-sm font-semibold text-slate-800 dark:text-white">Quick Start with URL</h4>
              <span className={`ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full ${
                colorTheme === 'purple' ? 'bg-purple-100 dark:bg-purple-500/20 text-purple-600' :
                colorTheme === 'blue' ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-600' :
                'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600'
              }`}>Optional</span>
            </div>
            <div className="flex gap-2">
              <input type="url" name="website_url" value={formData.website_url} onChange={handleInputChange} placeholder="https://example.com/product" className={`flex-1 ${inputClass}`} data-testid="website-url-input" />
              <button type="button" onClick={handleExtractWebsite} disabled={isExtractingWebsite || !formData.website_url?.trim()} className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all flex items-center gap-2 shrink-0 ${
                isExtractingWebsite || !formData.website_url?.trim()
                  ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                  : colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-500/20'
                  : colorTheme === 'blue' ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/20'
              }`} data-testid="fetch-content-btn">
                {isExtractingWebsite ? 'Extracting...' : 'Fetch Content'}
              </button>
            </div>
            {websiteExtractionError && <p className="text-xs text-red-500 mt-2">{websiteExtractionError}</p>}
            {websiteExtractionSuccess && <p className="text-xs text-emerald-500 mt-2">Product info extracted successfully!</p>}
            
            {/* Brand Logo Detection Chip */}
            {formData.brand_logo_url && (
              <div className="mt-3 flex items-center gap-3 p-3 rounded-xl bg-white/70 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700" data-testid="brand-logo-chip">
                <div className="w-12 h-12 rounded-lg bg-slate-50 dark:bg-slate-900 flex items-center justify-center overflow-hidden shrink-0 border border-slate-200 dark:border-slate-700">
                  <img
                    src={formData.brand_logo_url.startsWith('http') ? formData.brand_logo_url : `${process.env.REACT_APP_BACKEND_URL}${formData.brand_logo_url}`}
                    alt="Brand logo"
                    className="w-full h-full object-contain"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    data-testid="brand-logo-preview"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800 dark:text-white truncate">Brand logo detected</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Will be embedded into your ad (Ultra Image auto-selected)
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer shrink-0" data-testid="use-brand-logo-toggle">
                  <input
                    type="checkbox"
                    checked={!!formData.brand_logo_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, brand_logo_url: e.target.checked ? prev.brand_logo_url : '' }))}
                    className="sr-only peer"
                  />
                  <div className="relative w-10 h-5 bg-slate-300 dark:bg-slate-600 peer-checked:bg-emerald-500 rounded-full transition-colors">
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${formData.brand_logo_url ? 'translate-x-5' : ''}`}></div>
                  </div>
                  <span className="text-[11px] text-slate-600 dark:text-slate-300">Use</span>
                </label>
              </div>
            )}
          </div>

          {/* Product Information */}
          <div className={cardClass} data-testid="product-info-section">
            <h4 className={`${titleClass} mb-5`}>Product Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Product Name *</label>
                <input type="text" name="product_name" value={formData.product_name} onChange={handleInputChange} required className={inputClass} placeholder="e.g., EcoClean Laundry Detergent" data-testid="product-name-input" />
              </div>
              <div>
                <label className={labelClass}>Price (Optional)</label>
                <input type="text" name="price" value={formData.price} onChange={handleInputChange} className={inputClass} placeholder="e.g., $19.99" data-testid="price-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className={labelClass}>Category</label>
                <input type="text" name="category" value={formData.category} onChange={handleInputChange} className={inputClass} placeholder="e.g., Household, Electronics" data-testid="category-input" />
              </div>
              <div>
                <label className={labelClass}>Brand Voice</label>
                <select name="brand_voice" value={formData.brand_voice} onChange={handleInputChange} className={inputClass} data-testid="brand-voice-select">
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="casual">Casual</option>
                  <option value="luxury">Luxury</option>
                  <option value="playful">Playful</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className={labelClass}>Product Description *</label>
              <textarea name="product_description" value={formData.product_description} onChange={handleInputChange} required rows="3" className={inputClass} placeholder="Describe your product, features, and benefits..." data-testid="product-description-input" />
            </div>
            {(template.id === 'image_promo' || template.id === 'video_promo') && (
              <>
                <div className="mt-4">
                  <label className={labelClass}>Product Tagline (Optional)</label>
                  <input type="text" name="product_tagline" value={formData.product_tagline} onChange={handleInputChange} className={inputClass} placeholder="e.g., Smarter Ads. Better Leads." />
                </div>
                <BrandAssetsCard
                  formData={formData}
                  setFormData={setFormData}
                  sessionToken={sessionToken}
                  testIdPrefix="quick-brand-assets"
                />
              </>
            )}
          </div>

          {/* Geography & Language */}
          <div className={`${cardClass} relative z-20`} data-testid="geography-section">
            <h4 className={`${titleClass} mb-5`}>Geography & Language</h4>
            <GeographyLanguageSelector
              selectedGeographies={selectedGeographies}
              onGeographyChange={setSelectedGeographies}
              selectedLanguages={selectedLanguages}
              onLanguageChange={setSelectedLanguages}
            />
            {/* Localized Images Toggle — Ultra Image, Nano Banana Pro & Nano Banana 2 all render multilingual text well */}
            {['gpt4o-image', 'nano-banana-pro', 'nano-banana-2'].includes(formData.image_model) && selectedLanguages.length > 1 && (
              <div className="mt-3 flex items-start gap-3 p-3 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800" data-testid="localized-images-toggle-section">
                <label className="flex items-center gap-2 cursor-pointer shrink-0 mt-1" data-testid="generate-localized-images-toggle">
                  <input
                    type="checkbox"
                    checked={!!formData.generate_localized_images}
                    onChange={(e) => setFormData(prev => ({ ...prev, generate_localized_images: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="relative w-10 h-5 bg-slate-300 dark:bg-slate-600 peer-checked:bg-blue-500 rounded-full transition-colors">
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${formData.generate_localized_images ? 'translate-x-5' : ''}`}></div>
                  </div>
                </label>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800 dark:text-white">Generate Localized Images ({selectedLanguages.length}× images)</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                    {formData.generate_localized_images
                      ? `Will generate ${selectedLanguages.length} images — one per language with native on-image text.`
                      : `Currently generating 1 image with English text. Enable to localize on-image text into each language.`}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Campaign Strategy Section */}
          <CampaignStrategy
            values={{
              campaign_objective: formData.campaign_objective,
              primary_platform: formData.primary_platform,
              cta_goal: formData.cta_goal,
              offer_incentive: formData.offer_incentive,
              landing_page_url: formData.landing_page_url,
              pain_point: formData.pain_point,
            }}
            onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
            productCategory={formData.category}
            productDescription={formData.product_description}
          />

          {/* Persona-Driven Optimization */}
          <div className={cardClass} data-testid="persona-section">
            <div className="flex items-center justify-between mb-5">
              <h4 className={titleClass}>Persona Optimization</h4>
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Enable</label>
                <button type="button" onClick={() => handleInputChange({ target: { name: 'enable_personas', value: !formData.enable_personas } })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                    formData.enable_personas
                      ? colorTheme === 'purple' ? 'bg-purple-600' : colorTheme === 'blue' ? 'bg-blue-600' : 'bg-emerald-600'
                      : 'bg-slate-200 dark:bg-slate-600'
                  }`} data-testid="enable-personas-toggle">
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${formData.enable_personas ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>

            {formData.enable_personas && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={labelClass}>Age Range</label>
                    <div className="flex items-center gap-2">
                      <input type="number" name="age_range_min" value={formData.age_range_min} onChange={handleInputChange} min="15" max="65" className={`w-20 !px-2 !py-2 text-center ${inputClass}`} />
                      <span className="text-slate-400 text-xs">to</span>
                      <input type="number" name="age_range_max" value={formData.age_range_max} onChange={handleInputChange} min="15" max="65" className={`w-20 !px-2 !py-2 text-center ${inputClass}`} />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Gender</label>
                    <select name="gender" value={formData.gender} onChange={handleInputChange} className={inputClass}>
                      <option value="All">All</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Industry</label>
                    <input type="text" name="industry_business_type" value={formData.industry_business_type} onChange={handleInputChange} className={inputClass} placeholder="e.g., Technology" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Budget Range</label>
                    <select name="budget_range" value={formData.budget_range} onChange={handleInputChange} className={inputClass}>
                      <option value="">Select budget {formData.geography ? `(${currentBudgetOptions.currency})` : ''}</option>
                      {currentBudgetOptions.ranges.map((range, i) => <option key={i} value={range}>{range}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Persona Count</label>
                    <select name="persona_count" value={formData.persona_count} onChange={handleInputChange} className={inputClass}>
                      <option value="ai_determined">AI Determined (Recommended)</option>
                      <option value="1">1 Persona</option>
                      <option value="2">2 Personas</option>
                      <option value="3">3 Personas</option>
                      <option value="4">4 Personas</option>
                      <option value="5">5 Personas</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Interests & Behaviors</label>
                  <textarea name="interests_behaviors" value={formData.interests_behaviors} onChange={handleInputChange} rows="2" className={inputClass} placeholder="e.g., Tech enthusiasts, Online shoppers" />
                </div>
              </div>
            )}
          </div>

          {/* Image Specifications */}
          {(template.id === 'image_promo' || template.id === 'telegram_ad') && (
            <div className={cardClass} data-testid="image-specs-section">
              <h4 className={`${titleClass} mb-5`}>Image Specifications</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>AI Image Model</label>
                  <select name="image_model" value={formData.image_model || 'freepik'} onChange={handleInputChange} className={inputClass}>
                    <option value="nebius">Quick Image</option>
                    <option value="kie-ai">Standard Image</option>
                    <option value="freepik">Best Value (Recommended)</option>
                    <option value="nano-banana-pro">Premium Image</option>
                    <option value="gpt4o-image">Ultra Image</option>
                  </select>
                  <p className="mt-1 text-[11px] text-slate-400">
                    {formData.image_model === 'gpt4o-image' ? 'Best quality with accurate text rendering' : formData.image_model === 'nano-banana-pro' ? '4K ultra-high definition' : formData.image_model === 'kie-ai' ? 'Standard quality, fast generation' : formData.image_model === 'freepik' ? 'Premium quality, cost-effective' : 'Very fast with good quality'}
                  </p>
                </div>
                {formData.image_model === 'nano-banana-pro' && (
                  <div>
                    <label className={labelClass}>Resolution</label>
                    <select name="nano_banana_resolution" value={formData.nano_banana_resolution || '4K'} onChange={handleInputChange} className={inputClass}>
                      <option value="1K">Standard (1K) - 18 credits</option>
                      <option value="2K">High (2K) - 18 credits</option>
                      <option value="4K">Ultra HD (4K) - 24 credits</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Video Specifications */}
          {template.id === 'video_promo' && (
            <div className={cardClass} data-testid="video-specs-section">
              <h4 className={`${titleClass} mb-5`}>Video Specifications</h4>
              <div className="mb-5">
                <VideoPlatformPicker
                  selected={formData.video_platform_preset}
                  formData={formData}
                  onApply={(patched) => setFormData((prev) => ({ ...prev, ...patched }))}
                  recommendedKey={getSuggestedPresetForPlatform(formData.primary_platform)}
                  recommendedSourceLabel={getPlatformLabel(formData.primary_platform)}
                  dense
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className={labelClass}>AI Model</label>
                  <select name="video_model" value={formData.video_model} onChange={handleInputChange} className={inputClass}>
                    <option value="freepik">Quick Video</option>
                    <option value="veo3_fast">Fast Video</option>
                    <option value="veo3">Premium Video</option>
                    <option value="ultra_video">Ultra Video</option>
                    <option value="gemini_omni_video">Ultra Pro (Recommended)</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Duration</label>
                  <select name="video_duration" value={formData.video_duration} onChange={handleInputChange} className={inputClass}>
                    {formData.video_model === 'ultra_video' ? (
                      <><option value={10}>10 seconds</option><option value={15}>15 seconds</option></>
                    ) : formData.video_model === 'gemini_omni_video' ? (
                      <><option value={4}>4 seconds</option><option value={6}>6 seconds</option><option value={8}>8 seconds</option><option value={10}>10 seconds (Recommended)</option></>
                    ) : (
                      <><option value={4}>4 seconds</option><option value={6}>6 seconds</option><option value={8}>8 seconds</option></>
                    )}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Resolution</label>
                  <select name="video_resolution" value={formData.video_resolution} onChange={handleInputChange} className={inputClass}>
                    <option value="720p">720p (HD) - 50 credits</option>
                    {(formData.video_model === 'veo3' || formData.video_model === 'veo3_fast') && <option value="1080p">1080p (Full HD) - 60 credits</option>}
                    {formData.video_model === 'ultra_video' && <option value="1080p">1080p (Full HD) - 75 credits</option>}
                    {formData.video_model === 'gemini_omni_video' && <option value="1080p">1080p (Full HD) - 80 credits</option>}
                    {(formData.video_model === 'veo3' || formData.video_model === 'veo3_fast') && <option value="4K">4K (Ultra HD) - 100 credits</option>}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Aspect Ratio</label>
                  <select name="video_aspect_ratio" value={formData.video_aspect_ratio} onChange={handleInputChange} className={inputClass}>
                    <option value="16:9">16:9 (Landscape)</option>
                    <option value="1:1">1:1 (Square)</option>
                    {formData.video_model === 'gemini_omni_video' && <option value="9:16">9:16 (Vertical / Reels)</option>}
                  </select>
                </div>
              </div>
              
              {/* Video Generation Mode picker — shown when at least one brand
                  asset is uploaded (product / avatar / logo). Auto-suggests
                  multi_reference when 2+ refs exist. */}
              {((formData.product_images?.length || 0) > 0 || formData.avatar_image_url || formData.brand_logo_url) && (
                <div className="mt-5 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg" data-testid="quick-video-mode-panel">
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">🎬 Video Mode</label>
                  <div className="space-y-3">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="radio"
                        name="video_generation_mode"
                        value="text"
                        checked={formData.video_generation_mode === 'text'}
                        onChange={handleInputChange}
                        className="mt-1 mr-3 text-green-600 focus:ring-green-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">Text-to-Video</span>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Ignore uploads, use prompt only.</p>
                      </div>
                    </label>
                    <label className="flex items-start cursor-pointer" data-testid="quick-video-mode-multi-ref">
                      <input
                        type="radio"
                        name="video_generation_mode"
                        value="multi_reference"
                        checked={formData.video_generation_mode === 'multi_reference'}
                        onChange={handleInputChange}
                        className="mt-1 mr-3 text-green-600 focus:ring-green-500"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          Multi-Reference Video <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 ml-1">RECOMMENDED</span>
                        </span>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Embed your product, portrait & logo in the video.</p>
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {(formData.product_images?.length || 0) > 0 && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">Product</span>
                          )}
                          {formData.avatar_image_url && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">Avatar</span>
                          )}
                          {formData.brand_logo_url && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">Logo</span>
                          )}
                        </div>
                        <p className="text-[11px] text-yellow-600 dark:text-yellow-400 mt-1">⚠️ Auto-forces Fast model + 16:9</p>
                      </div>
                    </label>
                  </div>
                  {formData.video_generation_mode !== 'text' && (
                    <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700/50" data-testid="quick-render-text-row">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          name="render_text_in_video"
                          checked={!!formData.render_text_in_video}
                          onChange={(e) => setFormData((p) => ({ ...p, render_text_in_video: e.target.checked }))}
                          className="mt-1 mr-3 text-green-600 focus:ring-green-500"
                          data-testid="quick-render-text-checkbox"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">Render text in video</span>
                          <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-0.5 leading-snug">
                            ⚠️ Veo 3 often <strong>mis-spells brand names</strong>. Keep OFF to let your logo carry typography crisply.
                          </p>
                        </div>
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 rounded-xl p-4 text-sm text-red-700 dark:text-red-300" data-testid="error-message">
              {error}
            </div>
          )}

          {/* Additional instructions (optional) — modular FeedbackInput */}
          <details className="group">
            <summary className="cursor-pointer text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white flex items-center gap-1.5 select-none">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-open:rotate-90"><polyline points="9 18 15 12 9 6"/></svg>
              <span>Additional instructions</span>
              <span className="text-xs text-slate-400 font-normal">(optional)</span>
            </summary>
            <div className="mt-2 pl-5">
              <FeedbackInput
                value={formData.user_feedback || ''}
                onChange={(v) => setFormData(prev => ({ ...prev, user_feedback: v }))}
                label=""
                optionalText=""
                rows={3}
                testIdBase="quick-content-feedback"
                helperText="Anything to fix, avoid, or emphasise? The AI will incorporate this when generating."
              />
            </div>
          </details>

          {/* Actions */}
          <div className="flex gap-3">
            <button type="button" onClick={onCancel} disabled={isSubmitting} className="px-6 py-3.5 rounded-2xl text-sm font-semibold border-2 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50" data-testid="cancel-btn">
              Cancel
            </button>
            <button type="button" onClick={() => setShowAdPreview(true)} className={`px-5 py-3.5 rounded-2xl text-sm font-semibold transition-all border-2 flex items-center gap-2 ${
              colorTheme === 'purple' ? 'border-purple-300 dark:border-purple-600 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20' :
              colorTheme === 'blue' ? 'border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20' :
              'border-emerald-300 dark:border-emerald-600 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
            }`} data-testid="preview-ads-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
              Preview
            </button>
            <button type="submit" disabled={isSubmitting} className={`flex-1 bg-gradient-to-r ${
              colorTheme === 'purple' ? 'from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-purple-500/20' :
              colorTheme === 'blue' ? 'from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 shadow-blue-500/20' :
              'from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 shadow-emerald-500/20'
            } text-white font-bold py-3.5 px-8 rounded-2xl text-sm transition-all shadow-lg flex items-center justify-center gap-2 hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed`} data-testid="generate-btn">
              {isSubmitting ? `Generating ${template.title}...` : `Generate ${template.title}`}
            </button>
          </div>
        </form>

        {/* Ad Preview Panel */}
        <AdPreviewPanel
          formData={formData}
          selectedAdTypes={[template.id]}
          isOpen={showAdPreview}
          onClose={() => setShowAdPreview(false)}
          colorTheme={colorTheme}
        />
      </div>
    </div>
  );
};

export default QuickContentFormV2;
